import { projectTitleCaughtIds } from "../js/fishydex/title-progress.js";
import {
  EMPTY_SEARCH_EXPRESSION,
  resolveSearchExpression,
} from "./map-search-contract.js";
import { FISHYMAP_POINT_ICON_SCALE_DEFAULT } from "./map-bridge-contract.js";
import { normalizeRestoreView } from "./map-bridge-input.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";
import {
  DEFAULT_ENABLED_LAYER_IDS,
  DEFAULT_LAYER_CLIP_MASKS,
  DEFAULT_WINDOW_UI_STATE,
} from "./map-signal-contract.js";
import {
  DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
  normalizeVisibleTradeNpcClasses,
} from "./map-trade-npc-contract.js";

export { DEFAULT_ENABLED_LAYER_IDS };

export const MAP_UI_STORAGE_KEY = "fishystuff.map.window_ui.v1";
export const MAP_BOOKMARKS_STORAGE_KEY = "fishystuff.map.bookmarks.v1";

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOwnKey(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function stripEmptyRestorePatchBranches(patch) {
  if (!isPlainObject(patch)) {
    return null;
  }
  if (isPlainObject(patch._map_bridged?.ui) && !Object.keys(patch._map_bridged.ui).length) {
    delete patch._map_bridged.ui;
  }
  if (
    isPlainObject(patch._map_bridged?.filters) &&
    !Object.keys(patch._map_bridged.filters).length
  ) {
    delete patch._map_bridged.filters;
  }
  if (isPlainObject(patch._map_bridged) && !Object.keys(patch._map_bridged).length) {
    delete patch._map_bridged;
  }
  if (isPlainObject(patch._map_ui?.windowUi) && !Object.keys(patch._map_ui.windowUi).length) {
    delete patch._map_ui.windowUi;
  }
  if (isPlainObject(patch._map_ui?.layers) && !Object.keys(patch._map_ui.layers).length) {
    delete patch._map_ui.layers;
  }
  if (isPlainObject(patch._map_ui?.search) && !Object.keys(patch._map_ui.search).length) {
    delete patch._map_ui.search;
  }
  if (isPlainObject(patch._map_ui) && !Object.keys(patch._map_ui).length) {
    delete patch._map_ui;
  }
  if (Array.isArray(patch._map_bookmarks?.entries) && !patch._map_bookmarks.entries.length) {
    delete patch._map_bookmarks;
  }
  if (isPlainObject(patch._map_session) && !Object.keys(patch._map_session).length) {
    delete patch._map_session;
  }
  return Object.keys(patch).length ? patch : null;
}

function normalizeLayerBooleanRecord(value) {
  if (!isPlainObject(value)) {
    return {};
  }
  const normalized = {};
  for (const [key, enabled] of Object.entries(value)) {
    const normalizedKey = String(key || "").trim();
    if (!normalizedKey) {
      continue;
    }
    if (typeof enabled === "boolean") {
      normalized[normalizedKey] = enabled;
      continue;
    }
    if (isPlainObject(enabled)) {
      const nested = normalizeLayerBooleanRecord(enabled);
      if (Object.keys(nested).length) {
        normalized[normalizedKey] = nested;
      }
    }
  }
  return normalized;
}

function hasMapPresetCamera(view) {
  if (!isPlainObject(view)) {
    return false;
  }
  const camera = isPlainObject(view.camera) ? view.camera : {};
  return Object.keys(camera).length > 0;
}

function mapPresetPayloadWithoutOrdinaryCamera(payload) {
  const normalized = normalizeMapPresetPayload(payload);
  return {
    ...normalized,
    view: {
      ...normalized.view,
      camera: isPlainObject(normalized.view?.camera) ? normalized.view.camera : {},
    },
  };
}

export function mapPresetPayloadsEqual(left, right) {
  const leftPayload = mapPresetPayloadWithoutOrdinaryCamera(left);
  const rightPayload = mapPresetPayloadWithoutOrdinaryCamera(right);
  if (
    leftPayload.view.viewMode === rightPayload.view.viewMode
    && (!hasMapPresetCamera(leftPayload.view) || !hasMapPresetCamera(rightPayload.view))
  ) {
    leftPayload.view = { ...leftPayload.view, camera: {} };
    rightPayload.view = { ...rightPayload.view, camera: {} };
  }
  return JSON.stringify(leftPayload) === JSON.stringify(rightPayload);
}

function mapPresetViewMode(value) {
  return value === "3d" ? "3d" : "2d";
}

function stripQueryOwnedRestoreFields(patch, locationHref) {
  if (!isPlainObject(patch) || !locationHref) {
    return patch;
  }
  let params;
  try {
    params = new URL(locationHref, "https://fishystuff.fish").searchParams;
  } catch (_error) {
    return patch;
  }
  const nextPatch = cloneJson(patch);
  const search = nextPatch._map_ui?.search;
  const bridgedFilters = nextPatch._map_bridged?.filters;
  const hasExpressionQueryParam = params.has("expr");

  if (search && hasExpressionQueryParam) {
    delete search.expression;
  }
  if (bridgedFilters) {
    if (params.has("layers")) {
      delete bridgedFilters.layerIdsVisible;
    }
  }

  return stripEmptyRestorePatchBranches(nextPatch);
}

function storedMapPageFields({ ui, bridged, bookmarks } = {}) {
  const windowUi = ui?.windowUi;
  const search = ui?.search;
  const bridgedUi = bridged?.ui;
  const bridgedFilters = bridged?.filters;
  const searchExpression = resolveSearchExpression(search?.expression);
  const bookmarkEntries = Array.isArray(bookmarks?.entries)
    ? cloneJson(bookmarks.entries)
    : [];
  return {
    ui: {
      windowUi: isPlainObject(windowUi) ? cloneJson(windowUi) : {},
      layers: {
        expandedLayerIds: Array.isArray(ui?.layers?.expandedLayerIds)
          ? cloneJson(ui.layers.expandedLayerIds)
          : [],
        hoverFactsVisibleByLayer: isPlainObject(ui?.layers?.hoverFactsVisibleByLayer)
          ? cloneJson(ui.layers.hoverFactsVisibleByLayer)
          : {},
        sampleHoverVisibleByLayer: isPlainObject(ui?.layers?.sampleHoverVisibleByLayer)
          ? cloneJson(ui.layers.sampleHoverVisibleByLayer)
          : {},
        lineagePanelVisibleByLayer: isPlainObject(
          ui?.layers?.lineagePanelVisibleByLayer,
        )
          ? cloneJson(ui.layers.lineagePanelVisibleByLayer)
          : {},
      },
      search: {
        expression: cloneJson(searchExpression),
      },
    },
    bookmarks: {
      entries: bookmarkEntries,
    },
    bridged: {
      ui: {
        showPoints: bridgedUi?.showPoints !== false,
        showPointIcons: bridgedUi?.showPointIcons !== false,
        viewMode: bridgedUi?.viewMode === "3d" ? "3d" : "2d",
        pointIconScale: Number.isFinite(bridgedUi?.pointIconScale)
          ? Number(bridgedUi.pointIconScale)
          : FISHYMAP_POINT_ICON_SCALE_DEFAULT,
      },
      filters: {
        layerIdsVisible: Array.isArray(bridgedFilters?.layerIdsVisible)
          ? cloneJson(bridgedFilters.layerIdsVisible)
          : cloneJson(DEFAULT_ENABLED_LAYER_IDS),
        layerIdsOrdered: Array.isArray(bridgedFilters?.layerIdsOrdered)
          ? cloneJson(bridgedFilters.layerIdsOrdered)
          : [],
        layerFilterBindingIdsDisabledByLayer: isPlainObject(
          bridgedFilters?.layerFilterBindingIdsDisabledByLayer,
        )
          ? cloneJson(bridgedFilters.layerFilterBindingIdsDisabledByLayer)
          : {},
        layerOpacities: isPlainObject(bridgedFilters?.layerOpacities)
          ? cloneJson(bridgedFilters.layerOpacities)
          : {},
        layerHighlights: isPlainObject(bridgedFilters?.layerHighlights)
          ? cloneJson(bridgedFilters.layerHighlights)
          : {},
        layerConfidenceShowcase: isPlainObject(bridgedFilters?.layerConfidenceShowcase)
          ? cloneJson(bridgedFilters.layerConfidenceShowcase)
          : {},
        layerClipMasks: isPlainObject(bridgedFilters?.layerClipMasks)
          ? cloneJson(bridgedFilters.layerClipMasks)
          : {},
        layerWaypointConnectionsVisible: isPlainObject(
          bridgedFilters?.layerWaypointConnectionsVisible,
        )
          ? cloneJson(bridgedFilters.layerWaypointConnectionsVisible)
          : {},
        layerWaypointLabelsVisible: isPlainObject(bridgedFilters?.layerWaypointLabelsVisible)
          ? cloneJson(bridgedFilters.layerWaypointLabelsVisible)
          : {},
        layerPointIconsVisible: isPlainObject(bridgedFilters?.layerPointIconsVisible)
          ? cloneJson(bridgedFilters.layerPointIconsVisible)
          : {},
        layerPointIconScales: isPlainObject(bridgedFilters?.layerPointIconScales)
          ? cloneJson(bridgedFilters.layerPointIconScales)
          : {},
        layerPointClusteringEnabled: isPlainObject(
          bridgedFilters?.layerPointClusteringEnabled,
        )
          ? cloneJson(bridgedFilters.layerPointClusteringEnabled)
          : {},
        tradeNpcClassesVisible: normalizeVisibleTradeNpcClasses(
          bridgedFilters?.tradeNpcClassesVisible ?? DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
        ),
        filterOnlyActiveConditionGroups:
          bridgedFilters?.filterOnlyActiveConditionGroups !== false,
        lootConditionSelection: isPlainObject(bridgedFilters?.lootConditionSelection)
          ? cloneJson(bridgedFilters.lootConditionSelection)
          : {},
      },
    },
  };
}

function uiStorageSnapshot(stored) {
  const bridgedUi = isPlainObject(stored?.bridged?.ui) ? stored.bridged.ui : {};
  const bridgedFilters = isPlainObject(stored?.bridged?.filters)
    ? stored.bridged.filters
    : {};
  const searchExpression = resolveSearchExpression(stored?.ui?.search?.expression);
  const windowUi = isPlainObject(stored?.ui?.windowUi)
    ? cloneJson(stored.ui.windowUi)
    : {};
  if (isPlainObject(windowUi.settings)) {
    delete windowUi.settings.lootRateDisplayMode;
    delete windowUi.settings.normalizeRates;
  }
  return {
    windowUi,
    layers: {
      expandedLayerIds: Array.isArray(stored?.ui?.layers?.expandedLayerIds)
        ? cloneJson(stored.ui.layers.expandedLayerIds)
        : [],
      hoverFactsVisibleByLayer: normalizeLayerBooleanRecord(
        stored?.ui?.layers?.hoverFactsVisibleByLayer,
      ),
      sampleHoverVisibleByLayer: normalizeLayerBooleanRecord(
        stored?.ui?.layers?.sampleHoverVisibleByLayer,
      ),
      lineagePanelVisibleByLayer: normalizeLayerBooleanRecord(
        stored?.ui?.layers?.lineagePanelVisibleByLayer,
      ),
    },
    search: {
      expression: cloneJson(searchExpression),
    },
    bridgedUi: {
      showPoints: bridgedUi.showPoints !== false,
      showPointIcons: bridgedUi.showPointIcons !== false,
      viewMode: bridgedUi.viewMode === "3d" ? "3d" : "2d",
      pointIconScale: Number.isFinite(bridgedUi.pointIconScale)
        ? Number(bridgedUi.pointIconScale)
        : FISHYMAP_POINT_ICON_SCALE_DEFAULT,
    },
    bridgedFilters: {
      layerIdsVisible: Array.isArray(bridgedFilters.layerIdsVisible)
        ? cloneJson(bridgedFilters.layerIdsVisible)
        : cloneJson(DEFAULT_ENABLED_LAYER_IDS),
      layerIdsOrdered: Array.isArray(bridgedFilters.layerIdsOrdered)
        ? cloneJson(bridgedFilters.layerIdsOrdered)
        : [],
      layerFilterBindingIdsDisabledByLayer: isPlainObject(
        bridgedFilters.layerFilterBindingIdsDisabledByLayer,
      )
        ? cloneJson(bridgedFilters.layerFilterBindingIdsDisabledByLayer)
        : {},
      layerOpacities: isPlainObject(bridgedFilters.layerOpacities)
        ? cloneJson(bridgedFilters.layerOpacities)
        : {},
      layerHighlights: isPlainObject(bridgedFilters.layerHighlights)
        ? cloneJson(bridgedFilters.layerHighlights)
        : {},
      layerConfidenceShowcase: isPlainObject(bridgedFilters.layerConfidenceShowcase)
        ? cloneJson(bridgedFilters.layerConfidenceShowcase)
        : {},
      layerClipMasks: isPlainObject(bridgedFilters.layerClipMasks)
        ? cloneJson(bridgedFilters.layerClipMasks)
        : {},
      layerWaypointConnectionsVisible: isPlainObject(bridgedFilters.layerWaypointConnectionsVisible)
        ? cloneJson(bridgedFilters.layerWaypointConnectionsVisible)
        : {},
      layerWaypointLabelsVisible: isPlainObject(bridgedFilters.layerWaypointLabelsVisible)
        ? cloneJson(bridgedFilters.layerWaypointLabelsVisible)
        : {},
      layerPointIconsVisible: isPlainObject(bridgedFilters.layerPointIconsVisible)
        ? cloneJson(bridgedFilters.layerPointIconsVisible)
        : {},
      layerPointIconScales: isPlainObject(bridgedFilters.layerPointIconScales)
        ? cloneJson(bridgedFilters.layerPointIconScales)
        : {},
      layerPointClusteringEnabled: isPlainObject(
        bridgedFilters.layerPointClusteringEnabled,
      )
        ? cloneJson(bridgedFilters.layerPointClusteringEnabled)
        : {},
      tradeNpcClassesVisible: normalizeVisibleTradeNpcClasses(
        bridgedFilters.tradeNpcClassesVisible ?? DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
      ),
      filterOnlyActiveConditionGroups:
        bridgedFilters.filterOnlyActiveConditionGroups !== false,
      lootConditionSelection: isPlainObject(bridgedFilters.lootConditionSelection)
        ? cloneJson(bridgedFilters.lootConditionSelection)
        : {},
    },
  };
}

function restoreUiPatch(parsed) {
  if (!isPlainObject(parsed)) {
    return null;
  }
  const patch = {};
  if (isPlainObject(parsed.windowUi)) {
    const windowUi = cloneJson(parsed.windowUi);
    if (isPlainObject(windowUi.settings)) {
      delete windowUi.settings.lootRateDisplayMode;
      delete windowUi.settings.normalizeRates;
    }
    patch._map_ui = { windowUi };
  }
  if (isPlainObject(parsed.layers)) {
    patch._map_ui = patch._map_ui || {};
    patch._map_ui.layers = {
      expandedLayerIds: Array.isArray(parsed.layers.expandedLayerIds)
        ? cloneJson(parsed.layers.expandedLayerIds)
        : [],
      hoverFactsVisibleByLayer: normalizeLayerBooleanRecord(parsed.layers.hoverFactsVisibleByLayer),
      sampleHoverVisibleByLayer: normalizeLayerBooleanRecord(parsed.layers.sampleHoverVisibleByLayer),
      lineagePanelVisibleByLayer: normalizeLayerBooleanRecord(
        parsed.layers.lineagePanelVisibleByLayer,
      ),
    };
  }
  const search = isPlainObject(parsed.search)
    ? parsed.search
    : {};
  const bridgedUi = isPlainObject(parsed.bridgedUi) ? parsed.bridgedUi : null;
  const bridgedFilters = isPlainObject(parsed.bridgedFilters) ? parsed.bridgedFilters : null;

  const hasStoredSearchSelection = Object.prototype.hasOwnProperty.call(search, "expression");
  const searchExpression = hasStoredSearchSelection
    ? resolveSearchExpression(search.expression)
    : null;
  if (Object.keys(search).length || searchExpression) {
    patch._map_ui = patch._map_ui || {};
    patch._map_ui.search = {
      ...(searchExpression
        ? {
            expression: cloneJson(searchExpression),
          }
        : {}),
    };
  }
  if (bridgedUi) {
    patch._map_bridged = patch._map_bridged || {};
    patch._map_bridged.ui = {
      showPoints: bridgedUi.showPoints !== false,
      showPointIcons: bridgedUi.showPointIcons !== false,
      viewMode: bridgedUi.viewMode === "3d" ? "3d" : "2d",
      pointIconScale: Number.isFinite(bridgedUi.pointIconScale)
        ? Number(bridgedUi.pointIconScale)
        : FISHYMAP_POINT_ICON_SCALE_DEFAULT,
    };
  }
  const hasPersistedLayerFilterState =
    bridgedFilters
    && [
      "layerIdsVisible",
      "layerIdsOrdered",
      "layerFilterBindingIdsDisabledByLayer",
      "layerOpacities",
      "layerHighlights",
      "layerConfidenceShowcase",
      "layerClipMasks",
      "layerWaypointConnectionsVisible",
      "layerWaypointLabelsVisible",
      "layerPointIconsVisible",
      "layerPointIconScales",
      "layerPointClusteringEnabled",
      "tradeNpcClassesVisible",
      "filterOnlyActiveConditionGroups",
      "lootConditionSelection",
    ].some((key) => hasOwnKey(bridgedFilters, key));

  if (hasPersistedLayerFilterState) {
    patch._map_bridged = patch._map_bridged || {};
    const filters = {
      layerIdsVisible: Array.isArray(bridgedFilters.layerIdsVisible)
        ? cloneJson(bridgedFilters.layerIdsVisible)
        : cloneJson(DEFAULT_ENABLED_LAYER_IDS),
      layerIdsOrdered: Array.isArray(bridgedFilters.layerIdsOrdered)
        ? cloneJson(bridgedFilters.layerIdsOrdered)
        : [],
      layerFilterBindingIdsDisabledByLayer: isPlainObject(
        bridgedFilters.layerFilterBindingIdsDisabledByLayer,
      )
        ? cloneJson(bridgedFilters.layerFilterBindingIdsDisabledByLayer)
        : {},
      layerOpacities: isPlainObject(bridgedFilters.layerOpacities)
        ? cloneJson(bridgedFilters.layerOpacities)
        : {},
      layerHighlights: isPlainObject(bridgedFilters.layerHighlights)
        ? cloneJson(bridgedFilters.layerHighlights)
        : {},
      layerConfidenceShowcase: isPlainObject(bridgedFilters.layerConfidenceShowcase)
        ? cloneJson(bridgedFilters.layerConfidenceShowcase)
        : {},
      layerWaypointConnectionsVisible: isPlainObject(bridgedFilters.layerWaypointConnectionsVisible)
        ? cloneJson(bridgedFilters.layerWaypointConnectionsVisible)
        : {},
      layerWaypointLabelsVisible: isPlainObject(bridgedFilters.layerWaypointLabelsVisible)
        ? cloneJson(bridgedFilters.layerWaypointLabelsVisible)
        : {},
      layerPointIconsVisible: isPlainObject(bridgedFilters.layerPointIconsVisible)
        ? cloneJson(bridgedFilters.layerPointIconsVisible)
        : {},
      layerPointIconScales: isPlainObject(bridgedFilters.layerPointIconScales)
        ? cloneJson(bridgedFilters.layerPointIconScales)
        : {},
      layerPointClusteringEnabled: isPlainObject(
        bridgedFilters.layerPointClusteringEnabled,
      )
        ? cloneJson(bridgedFilters.layerPointClusteringEnabled)
        : {},
      tradeNpcClassesVisible: normalizeVisibleTradeNpcClasses(
        bridgedFilters.tradeNpcClassesVisible ?? DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
      ),
      filterOnlyActiveConditionGroups:
        bridgedFilters.filterOnlyActiveConditionGroups !== false,
      lootConditionSelection: isPlainObject(bridgedFilters.lootConditionSelection)
        ? cloneJson(bridgedFilters.lootConditionSelection)
        : {},
    };
    if (hasOwnKey(bridgedFilters, "layerClipMasks")) {
      filters.layerClipMasks = isPlainObject(bridgedFilters.layerClipMasks)
        ? cloneJson(bridgedFilters.layerClipMasks)
        : {};
    }
    patch._map_bridged.filters = filters;
  }
  return Object.keys(patch).length ? patch : null;
}

function ensureStoredMapPageFields(stored) {
  return stored && typeof stored === "object"
    ? stored
    : {
        ui: {},
        bridged: {},
        bookmarks: { entries: [] },
      };
}

function ensureUiSnapshot(stored) {
  return stored && typeof stored === "object"
    ? stored
    : {
        windowUi: {},
        layers: {
          expandedLayerIds: [],
          hoverFactsVisibleByLayer: {},
          sampleHoverVisibleByLayer: {},
          lineagePanelVisibleByLayer: {},
        },
        search: { expression: cloneJson(EMPTY_SEARCH_EXPRESSION) },
        bridgedUi: {
          showPoints: true,
          showPointIcons: true,
          viewMode: "2d",
          pointIconScale: FISHYMAP_POINT_ICON_SCALE_DEFAULT,
        },
        bridgedFilters: {},
      };
}

function defaultMapPresetSnapshot() {
  return {
    windowUi: cloneJson(DEFAULT_WINDOW_UI_STATE),
    layers: {
      expandedLayerIds: [],
      hoverFactsVisibleByLayer: {},
      sampleHoverVisibleByLayer: {},
      lineagePanelVisibleByLayer: {},
    },
    search: {
      expression: cloneJson(EMPTY_SEARCH_EXPRESSION),
    },
    bridgedUi: {
      showPoints: true,
      showPointIcons: true,
      viewMode: "2d",
      pointIconScale: FISHYMAP_POINT_ICON_SCALE_DEFAULT,
    },
    bridgedFilters: {
      layerIdsVisible: cloneJson(DEFAULT_ENABLED_LAYER_IDS),
      layerIdsOrdered: [],
      layerFilterBindingIdsDisabledByLayer: {},
      layerOpacities: {},
      layerHighlights: {},
      layerConfidenceShowcase: {},
      layerClipMasks: cloneJson(DEFAULT_LAYER_CLIP_MASKS),
      layerWaypointConnectionsVisible: {},
      layerWaypointLabelsVisible: {},
      layerPointIconsVisible: {},
      layerPointIconScales: {},
      layerPointClusteringEnabled: {},
      tradeNpcClassesVisible: cloneJson(DEFAULT_VISIBLE_TRADE_NPC_CLASSES),
      filterOnlyActiveConditionGroups: true,
      lootConditionSelection: {},
    },
    view: { viewMode: "2d", camera: {} },
  };
}

function mergeMapPresetBranch(defaultBranch, sourceBranch) {
  const merged = cloneJson(defaultBranch);
  if (!isPlainObject(sourceBranch)) {
    return merged;
  }
  for (const [key, value] of Object.entries(sourceBranch)) {
    if (value === undefined) {
      continue;
    }
    merged[key] = isPlainObject(value) && isPlainObject(merged[key])
      ? { ...merged[key], ...cloneJson(value) }
      : cloneJson(value);
  }
  return merged;
}

function stripMapPresetWindowState(windowUi) {
  const next = isPlainObject(windowUi) ? cloneJson(windowUi) : {};
  if (isPlainObject(next.settings)) {
    delete next.settings.open;
    delete next.settings.collapsed;
    delete next.settings.lootRateDisplayMode;
    delete next.settings.normalizeRates;
    delete next.settings.zoneLootSortBy;
    delete next.settings.zoneLootSortOrder;
  }
  return next;
}

export function normalizeMapPresetPayload(value) {
  const source = isPlainObject(value) ? value : {};
  const defaults = defaultMapPresetSnapshot();
  const sourceWindowUi = isPlainObject(source.windowUi)
    ? source.windowUi
    : source._map_ui?.windowUi;
  const sourceLayers = isPlainObject(source.layers)
    ? source.layers
    : source._map_ui?.layers;
  const sourceSearch = isPlainObject(source.search)
    ? source.search
    : source._map_ui?.search;
  const sourceBridgedUi = isPlainObject(source.bridgedUi)
    ? source.bridgedUi
    : source._map_bridged?.ui;
  const sourceBridgedFilters = isPlainObject(source.bridgedFilters)
    ? source.bridgedFilters
    : source._map_bridged?.filters;
  const windowUi = stripMapPresetWindowState(
    mergeMapPresetBranch(defaults.windowUi, sourceWindowUi),
  );
  const mergedSearch = mergeMapPresetBranch(defaults.search, sourceSearch);
  const uiSnapshot = uiStorageSnapshot({
    ui: {
      windowUi,
      layers: mergeMapPresetBranch(defaults.layers, sourceLayers),
      search: mergedSearch,
    },
    bridged: {
      ui: mergeMapPresetBranch(defaults.bridgedUi, sourceBridgedUi),
      filters: mergeMapPresetBranch(defaults.bridgedFilters, sourceBridgedFilters),
    },
  });
  const sourceView = isPlainObject(source.view)
    ? source.view
    : isPlainObject(source.session?.view)
      ? source.session.view
      : isPlainObject(source._map_session?.view)
        ? source._map_session.view
        : null;
  const view = normalizeRestoreView(sourceView || defaults.view);
  if (!Object.keys(view.camera).length) {
    view.viewMode = mapPresetViewMode(uiSnapshot.bridgedUi.viewMode);
  }
  return {
    version: 1,
    windowUi: cloneJson(uiSnapshot.windowUi),
    layers: cloneJson(uiSnapshot.layers),
    search: cloneJson(uiSnapshot.search),
    bridgedUi: cloneJson(uiSnapshot.bridgedUi),
    bridgedFilters: cloneJson(uiSnapshot.bridgedFilters),
    view,
  };
}

export function defaultMapPresetPayload() {
  return normalizeMapPresetPayload(defaultMapPresetSnapshot());
}

export function createMapPresetPayloadFromProjection(
  projection,
  { includeCamera = false, capturedView = null } = {},
) {
  const current = assertMapShellProjectionV1(projection);
  const stored = ensureStoredMapPageFields(storedMapPageFields({
    ui: current.ui,
    bridged: current.bridged,
    bookmarks: current.bookmarks,
  }));
  const uiSnapshot = uiStorageSnapshot(stored);
  const view = normalizeRestoreView(
    isPlainObject(capturedView)
      ? capturedView
      : isPlainObject(current.session?.view)
        ? current.session.view
        : defaultMapPresetSnapshot().view,
  );
  if (!includeCamera) {
    view.camera = {};
    view.viewMode = mapPresetViewMode(uiSnapshot.bridgedUi.viewMode);
  }
  return normalizeMapPresetPayload({
    ...uiSnapshot,
    view,
  });
}

export function mapPresetRestorePatch(payload) {
  const normalized = normalizeMapPresetPayload(payload);
  const patch = restoreUiPatch(normalized) || {};
  if (hasMapPresetCamera(normalized.view)) {
    patch._map_session = {
      ...(isPlainObject(patch._map_session) ? patch._map_session : {}),
      view: cloneJson(normalized.view),
    };
  }
  return patch;
}

function normalizeFallbackFishIds(values) {
  let ids = [];
  if (Array.isArray(values)) {
    ids = values;
  } else if (values && typeof values === "object") {
    ids = Object.entries(values)
      .filter((entry) => entry[1])
      .map((entry) => entry[0]);
  } else {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of ids) {
    const fishId = Number.parseInt(String(value), 10);
    if (!Number.isInteger(fishId) || fishId <= 0 || seen.has(fishId)) {
      continue;
    }
    seen.add(fishId);
    next.push(fishId);
  }
  return next.sort((left, right) => left - right);
}

export function projectFishydexCurrentSharedPayload(currentState, {
  fishCatalog = [],
  sharedFishState = null,
} = {}) {
  if (
    !isPlainObject(currentState)
    || !Array.isArray(currentState.catchCounts)
    || !Array.isArray(currentState.favouriteIds)
    || typeof currentState.showDried !== "boolean"
    || !isPlainObject(currentState.fishGuideProgress)
  ) {
    throw new TypeError("Map Fishydex projection requires typed Fishydex Current state");
  }
  const favouriteIds = normalizeFallbackFishIds(currentState.favouriteIds);
  const contract = sharedFishState
    ?? globalThis.window?.__fishystuffSharedFishState
    ?? globalThis.__fishystuffSharedFishState
    ?? null;
  const hasGuide = currentState.fishGuideProgress.status === "available";
  if (hasGuide && typeof contract?.createFishGuideCountLookup !== "function") {
    throw new TypeError("Map Fish Guide caught-state projector is unavailable");
  }
  if (!hasGuide && currentState.fishGuideProgress.status !== "unknown") {
    throw new TypeError("Map Fishydex projection received invalid Fish Guide progress");
  }
  const guideCountForKey = hasGuide
    ? contract.createFishGuideCountLookup(currentState.fishGuideProgress)
    : null;
  const unknownGuideCount = { kind: "unknown", count: null };
  const caughtIds = projectTitleCaughtIds(
    fishCatalog,
    currentState,
    (entry) => guideCountForKey
      ? guideCountForKey(entry.encyclopedia_key ?? entry.encyclopediaKey)
      : unknownGuideCount,
  );
  return {
    caughtIds: normalizeFallbackFishIds(caughtIds),
    favouriteIds,
    showDried: currentState.showDried,
  };
}

function currentDexDomainPayload(currentState, options) {
  if (currentState?.kind !== "ready") {
    return null;
  }
  return projectFishydexCurrentSharedPayload(currentState, options);
}

function buildSharedFishRestorePatch(
  fishydexCurrentState,
  { fishCatalog = [], sharedFishState = null } = {},
) {
  const currentFishydex = currentDexDomainPayload(fishydexCurrentState, {
    fishCatalog,
    sharedFishState,
  });
  return currentFishydex ? { _shared_fish: currentFishydex } : null;
}

export function loadSharedFishRestoreState({
  fishydexCurrentState,
  fishCatalog,
  sharedFishState,
} = {}) {
  return buildSharedFishRestorePatch(fishydexCurrentState, {
    fishCatalog,
    sharedFishState,
  });
}

export function loadRestoreState({
  localStorage,
  locationHref,
} = {}) {
  let uiPatch = null;
  let bookmarkPatch = null;
  try {
    const rawUi = localStorage?.getItem?.(MAP_UI_STORAGE_KEY);
    if (rawUi) {
      try {
        const parsedUi = JSON.parse(rawUi);
        uiPatch = stripQueryOwnedRestoreFields(restoreUiPatch(parsedUi), locationHref);
      } catch (_error) {
        localStorage?.removeItem?.(MAP_UI_STORAGE_KEY);
      }
    }
    const rawBookmarks = localStorage?.getItem?.(MAP_BOOKMARKS_STORAGE_KEY);
    if (rawBookmarks) {
      try {
        const parsed = JSON.parse(rawBookmarks);
        if (Array.isArray(parsed)) {
          bookmarkPatch = {
            _map_bookmarks: {
              entries: parsed,
            },
          };
        }
      } catch (_error) {
        localStorage?.removeItem?.(MAP_BOOKMARKS_STORAGE_KEY);
      }
    }
  } catch (_error) {
    uiPatch = null;
    bookmarkPatch = null;
  }
  return {
    uiPatch,
    bookmarkPatch,
  };
}

export function createMapPagePersistedStateFromProjection(projection) {
  const current = assertMapShellProjectionV1(projection);
  const stored = ensureStoredMapPageFields(storedMapPageFields({
    ui: current.ui,
    bridged: current.bridged,
    bookmarks: current.bookmarks,
  }));
  return {
    uiJson: JSON.stringify(ensureUiSnapshot(uiStorageSnapshot(stored))),
    bookmarksJson: JSON.stringify(stored.bookmarks.entries),
  };
}
